import React, { useEffect, useState } from 'react';
import { getIsAdmin } from '../api/authApi';
import { usersApi, type AppUser } from '../api/usersApi';
import { useLocationContext } from '../context/LocationContext';
import { Toast } from '../components/shared/Toast';
import { ConfirmDialog } from '../components/shared/ConfirmDialog';
import { CopyableId } from '../components/shared/CopyableId';
import { Dropdown } from '../components/mapping/Dropdown';

function practiceLabel(scope: string, locations: { ghl_id: string; name: string }[]): string {
    if (scope === 'all') return 'All practices';
    return locations.find(l => l.ghl_id === scope)?.name ?? scope;
}

export const UserManagementPage: React.FC = () => {
    const { locations } = useLocationContext();
    const [users, setUsers] = useState<AppUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

    const [inviteOpen, setInviteOpen] = useState(false);
    const [inviteUsername, setInviteUsername] = useState('');
    const [inviteName, setInviteName] = useState('');
    const [invitePracticeScope, setInvitePracticeScope] = useState('all');
    const [inviteBusy, setInviteBusy] = useState(false);
    const [inviteError, setInviteError] = useState<string | null>(null);

    const [reveal, setReveal] = useState<{ username: string; password: string } | null>(null);
    const [deactivateTarget, setDeactivateTarget] = useState<AppUser | null>(null);
    const [deactivateBusy, setDeactivateBusy] = useState(false);
    const [busyUserId, setBusyUserId] = useState<number | null>(null);

    const load = () => {
        setLoading(true);
        setError(null);
        usersApi.list()
            .then(setUsers)
            .catch(err => setError(err instanceof Error ? err.message : 'Failed to load users'))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    if (!getIsAdmin()) {
        return (
            <div className="mapper-ui font-sans text-mapper-text max-w-[1240px] mx-auto px-10 pt-[30px]">
                <div className="border border-mapper-red-border bg-mapper-red-bg-2 rounded-[12px] px-4 py-3 text-[13px] text-mapper-red">
                    Only admins can view this page.
                </div>
            </div>
        );
    }

    const handleInvite = async () => {
        const username = inviteUsername.trim();
        const name = inviteName.trim();
        if (!username) { setInviteError('Username is required'); return; }
        if (!name) { setInviteError('Name is required'); return; }
        setInviteBusy(true);
        setInviteError(null);
        try {
            const res = await usersApi.create(username, name, invitePracticeScope);
            setUsers(prev => [res.user, ...prev]);
            setInviteOpen(false);
            setInviteUsername('');
            setInviteName('');
            setInvitePracticeScope('all');
            setReveal({ username: res.user.username, password: res.generated_password });
        } catch (err) {
            setInviteError(err instanceof Error ? err.message : 'Failed to create user');
        } finally {
            setInviteBusy(false);
        }
    };

    const handleResetPassword = async (user: AppUser) => {
        setBusyUserId(user.id);
        try {
            const res = await usersApi.resetPassword(user.id);
            setReveal({ username: user.username, password: res.generated_password });
        } catch (err) {
            setToast({ message: err instanceof Error ? err.message : 'Failed to reset password', type: 'error' });
        } finally {
            setBusyUserId(null);
        }
    };

    const handleToggleActive = async () => {
        if (!deactivateTarget) return;
        setDeactivateBusy(true);
        try {
            const updated = await usersApi.update(deactivateTarget.id, { is_active: !deactivateTarget.is_active });
            setUsers(prev => prev.map(u => u.id === updated.id ? updated : u));
            setDeactivateTarget(null);
        } catch (err) {
            setToast({ message: err instanceof Error ? err.message : 'Failed to update user', type: 'error' });
        } finally {
            setDeactivateBusy(false);
        }
    };

    const handleScopeChange = async (user: AppUser, newScope: string) => {
        if (newScope === user.practice_scope) return;
        setBusyUserId(user.id);
        try {
            const updated = await usersApi.update(user.id, { practice_scope: newScope });
            setUsers(prev => prev.map(u => u.id === updated.id ? updated : u));
            setToast({ message: 'Practice access updated', type: 'success' });
        } catch (err) {
            setToast({ message: err instanceof Error ? err.message : 'Failed to update user', type: 'error' });
        } finally {
            setBusyUserId(null);
        }
    };

    return (
        <div className="mapper-ui font-sans text-mapper-text">
            <div className="max-w-[1240px] mx-auto px-10 pt-[30px] pb-[70px]">
                {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}

                <div className="flex items-end justify-between mb-[22px] gap-5">
                    <div>
                        <h1 className="text-[24px] font-extrabold tracking-[-0.5px] mb-[5px]">User Management</h1>
                        <p className="text-[14px] text-mapper-muted max-w-[640px]">
                            Invite teammates and control which practice's data they can see. Admin access stays with the primary login only.
                        </p>
                    </div>
                    <button
                        onClick={() => { setInviteOpen(true); setInviteError(null); }}
                        className="px-4 py-[10px] rounded-[10px] bg-mapper-teal text-white text-[13.5px] font-semibold shadow-mapper-btn"
                    >
                        Invite user
                    </button>
                </div>

                {error && (
                    <div className="border border-mapper-red-border bg-mapper-red-bg-2 rounded-[12px] px-4 py-3 mb-[22px] text-[13px] text-mapper-red">{error}</div>
                )}

                <div className="bg-mapper-white border border-mapper-border rounded-[15px] overflow-hidden">
                    <div className="grid grid-cols-[1.3fr_1.4fr_1.4fr_90px_1.1fr_1.5fr] items-center gap-[14px] px-[22px] py-3 border-b border-mapper-border-3 bg-mapper-surface text-[11px] font-bold tracking-[0.4px] text-mapper-muted-3">
                        <div>USERNAME</div>
                        <div>NAME</div>
                        <div>PRACTICE ACCESS</div>
                        <div>STATUS</div>
                        <div>INVITED BY</div>
                        <div>ACTIONS</div>
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center py-[52px] text-[14px] text-mapper-muted-2">Loading users…</div>
                    ) : users.length === 0 ? (
                        <div className="flex flex-col items-center gap-3 py-[52px] px-[22px] text-center">
                            <div className="text-[14px] font-semibold text-mapper-body-text-2">No users invited yet</div>
                        </div>
                    ) : (
                        users.map(user => (
                            <div key={user.id} className="grid grid-cols-[1.3fr_1.4fr_1.4fr_90px_1.1fr_1.5fr] items-center gap-[14px] px-[22px] py-[14px] border-b border-mapper-border-4 last:border-b-0">
                                <div className="text-[14px] font-bold truncate">{user.username}</div>
                                <div className="text-[13.5px] text-mapper-body-text truncate">{user.name ?? '—'}</div>
                                <div>
                                    <Dropdown
                                        compact
                                        searchable
                                        allLabel="All practices"
                                        value={user.practice_scope === 'all' ? '' : user.practice_scope}
                                        options={[
                                            ...locations.map(l => ({ value: l.ghl_id, label: l.name })),
                                            ...(user.practice_scope !== 'all' && !locations.some(l => l.ghl_id === user.practice_scope)
                                                ? [{ value: user.practice_scope, label: practiceLabel(user.practice_scope, locations) }]
                                                : []),
                                        ]}
                                        onChange={v => handleScopeChange(user, v === '' ? 'all' : v)}
                                    />
                                </div>
                                <div>
                                    <span className={`inline-flex items-center gap-[6px] text-[11px] font-bold px-[10px] py-[3px] rounded-full ${user.is_active ? 'bg-mapper-teal-bg-3 text-mapper-teal-dark' : 'bg-mapper-gray-bg text-mapper-body-text-2'}`}>
                                        {user.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                                <div className="text-[12.5px] text-mapper-muted-2 truncate">{user.created_by ?? '—'}</div>
                                <div className="flex items-center gap-[10px]">
                                    <button
                                        onClick={() => handleResetPassword(user)}
                                        disabled={busyUserId === user.id}
                                        className="text-[12.5px] font-semibold text-mapper-teal-hover disabled:opacity-50"
                                    >
                                        Reset password
                                    </button>
                                    <button
                                        onClick={() => setDeactivateTarget(user)}
                                        disabled={busyUserId === user.id}
                                        className={`text-[12.5px] font-semibold disabled:opacity-50 ${user.is_active ? 'text-mapper-red' : 'text-mapper-teal-hover'}`}
                                    >
                                        {user.is_active ? 'Deactivate' : 'Reactivate'}
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            <ConfirmDialog
                open={inviteOpen}
                title="Invite user"
                confirmLabel="Invite"
                busy={inviteBusy}
                onConfirm={handleInvite}
                onCancel={() => { setInviteOpen(false); setInviteError(null); }}
                message={
                    <div className="flex flex-col gap-[14px] text-left">
                        {inviteError && (
                            <div className="rounded-[9px] border border-mapper-red-border bg-mapper-red-bg-2 text-mapper-red text-[13px] px-3 py-[8px]">
                                {inviteError}
                            </div>
                        )}
                        <div>
                            <label className="block text-[13px] font-semibold text-mapper-body-text mb-[6px]">Name</label>
                            <input
                                value={inviteName}
                                onChange={e => setInviteName(e.target.value)}
                                placeholder="Enter full name"
                                autoFocus
                                className="w-full box-border px-3 py-[10px] border border-mapper-border-2 rounded-[10px] text-[14px] bg-mapper-white outline-none text-mapper-text"
                            />
                        </div>
                        <div>
                            <label className="block text-[13px] font-semibold text-mapper-body-text mb-[6px]">Username</label>
                            <input
                                value={inviteUsername}
                                onChange={e => setInviteUsername(e.target.value)}
                                placeholder="Enter username"
                                className="w-full box-border px-3 py-[10px] border border-mapper-border-2 rounded-[10px] text-[14px] bg-mapper-white outline-none text-mapper-text"
                            />
                        </div>
                        <div>
                            <label className="block text-[13px] font-semibold text-mapper-body-text mb-[6px]">Practice access</label>
                            <Dropdown
                                compact
                                searchable
                                allLabel="All practices (same access as admin)"
                                value={invitePracticeScope === 'all' ? '' : invitePracticeScope}
                                options={locations.map(l => ({ value: l.ghl_id, label: l.name }))}
                                onChange={v => setInvitePracticeScope(v === '' ? 'all' : v)}
                            />
                        </div>
                    </div>
                }
            />

            <ConfirmDialog
                open={!!reveal}
                title={reveal ? `Password for ${reveal.username}` : ''}
                confirmLabel="Done"
                cancelLabel="Close"
                onConfirm={() => setReveal(null)}
                onCancel={() => setReveal(null)}
                message={
                    <div className="flex flex-col gap-[10px] text-left">
                        <div className="text-[13px] text-mapper-muted-2">
                            Share this password with {reveal?.username} — it won't be shown again.
                        </div>
                        <div className="px-3 py-[10px] border border-mapper-border-2 rounded-[10px] bg-mapper-surface-2">
                            <CopyableId value={reveal?.password ?? ''} className="font-mono text-[15px] font-bold text-mapper-text" />
                        </div>
                    </div>
                }
            />

            <ConfirmDialog
                open={!!deactivateTarget}
                title={deactivateTarget?.is_active ? 'Deactivate user?' : 'Reactivate user?'}
                message={
                    deactivateTarget?.is_active
                        ? `${deactivateTarget?.username} will no longer be able to log in. You can reactivate them later.`
                        : `${deactivateTarget?.username} will be able to log in again.`
                }
                confirmLabel={deactivateTarget?.is_active ? 'Deactivate' : 'Reactivate'}
                tone={deactivateTarget?.is_active ? 'danger' : 'primary'}
                busy={deactivateBusy}
                onConfirm={handleToggleActive}
                onCancel={() => setDeactivateTarget(null)}
            />
        </div>
    );
};
